import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Button, Input, Checkbox } from '../atoms';
import { DataTable, StatCard } from '../organisms';
import { PageHeader } from '../shell';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * ReportViewer — /v2/reports/:slug
 *
 * Generic viewer for any of the reports shipped in the reporting engine:
 * active-appeals / bill-aging / reduction-summary / yoy-by-jurisdiction
 * / invoice-ledger / payments-register / value-yoy / savings-and-fees …
 *
 * Consumes `GET /api/reports/:slug` which returns:
 *   { slug, label, params: [{name, type, required, default}],
 *     columns: [{name, label, type}], rows: [...],
 *     meta: { generated_at, row_count } }
 *
 * Parameter controls (07.2): a report that declares `params` (e.g. value-yoy's
 * from_year / to_year year-over-year range) renders real inputs; Apply re-runs
 * `GET /api/reports/:slug?<params>` against the REAL report engine. The declared
 * param metadata is served by the run endpoint itself, so the same URL drives
 * the on-screen table AND the CSV / XLSX downloads (params flow through to both).
 *
 * Column selection (07.3): a "Columns" picker toggles visibility of the report's
 * real result columns client-side (money columns already dropped server-side for
 * money-blind roles never appear here, so the picker can't resurrect them).
 *
 * Phase 11 Block 11 PR 3 (2026-05-12); params + column selection (07.2/07.3).
 */

interface ReportColumn {
  name?: string;
  key?: string;
  label: string;
  type?: 'string' | 'number' | 'currency' | 'date';
}

interface ReportParam {
  name: string;
  type?: 'integer' | 'number' | 'text' | 'date';
  required?: boolean;
  default?: unknown;
}

interface ReportResponse {
  slug: string;
  label: string;
  description?: string;
  params?: ReportParam[];
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  meta?: {
    generated_at: string;
    row_count: number;
  };
}

// Backend columns come back as { key, label } from config/reports.js; the run
// handler serves them under those keys. Normalize to a single accessor name.
function columnName(col: ReportColumn): string {
  return col.name ?? col.key ?? '';
}

/**
 * report-row-clickable — make report result rows open the underlying record,
 * matching the entity-list DataTable interaction (onRowClick → detail route).
 *
 * A report row is navigable when it carries a known entity id column. The list
 * below is ordered by "primary record of the row": the FIRST id column a report
 * declares wins, so e.g. the Invoice Ledger (invoice_id + client_id) opens the
 * invoice, while parcel-centric reports (reduction-summary, active-appeals) open
 * the parcel. Reports with no id column (savings-and-fees, payments-register's
 * Stripe-only ids) stay non-clickable — no dead affordance.
 */
const REPORT_ID_ROUTES: Array<{ col: string; to: (id: string) => string }> = [
  { col: 'parcel_id', to: id => `/parcels/${id}` },
  { col: 'bill_id', to: id => `/tax-bills/${id}` },
  { col: 'invoice_id', to: id => `/invoices/${id}` },
  { col: 'appeal_id', to: id => `/appeals/${id}` },
  { col: 'tax_record_id', to: id => `/tax-records/${id}` },
  { col: 'client_id', to: id => `/clients/${id}` },
  { col: 'jurisdiction_id', to: id => `/jurisdictions/${id}` },
];

/** Build a `?a=1&b=2` suffix from applied params, skipping empty values. */
function paramQuery(applied: Record<string, string>): string {
  const entries = Object.entries(applied).filter(([, v]) => v !== '' && v != null);
  if (entries.length === 0) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of entries) qs.set(k, v);
  return `?${qs.toString()}`;
}

export function ReportViewer() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Draft = what the operator is editing; applied = what's actually been run.
  // Splitting them means the report only re-fetches on Apply, not per keystroke.
  const [draftParams, setDraftParams] = useState<Record<string, string>>({});
  const [appliedParams, setAppliedParams] = useState<Record<string, string>>({});
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const appliedQs = paramQuery(appliedParams);

  const query = useQuery<ReportResponse>({
    // appliedQs is part of the key so applying a new year range refetches.
    queryKey: ['report', slug, appliedQs],
    queryFn: ({ signal }) => api.get<ReportResponse>(`/api/reports/${slug}${appliedQs}`, signal),
    enabled: !!slug,
  });

  const reportColumns = query.data?.columns ?? [];

  // Row → record navigation. Resolve the report's primary id column once from
  // its schema; when present, clicking (or Enter/Space on) a row opens that
  // record's detail. Rows whose id value is empty simply don't navigate.
  const rowNav = useMemo<((row: Record<string, unknown>) => void) | undefined>(() => {
    const names = new Set(reportColumns.map(columnName));
    const match = REPORT_ID_ROUTES.find(r => names.has(r.col));
    if (!match) return undefined;
    return (row: Record<string, unknown>) => {
      const raw = row[match.col];
      if (raw === null || raw === undefined || raw === '') return;
      navigate(match.to(String(raw)));
    };
  }, [reportColumns, navigate]);

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    return reportColumns
      .filter(col => !hiddenCols.has(columnName(col)))
      .map(col => ({
        accessorKey: columnName(col),
        header: col.label,
        cell: ({ getValue }) => formatCell(col, getValue()),
      }));
  }, [reportColumns, hiddenCols]);

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Running {slug}…</p>;
  }

  if (query.error) {
    const notFound = query.error instanceof ApiClientError && query.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? `Report "${slug}" not found` : 'Failed to load report'}</h2>
        <p style={{ margin: 0 }}>{(query.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
            Back to Dashboard
          </Button>
        </p>
      </div>
    );
  }

  if (!query.data) return null;

  const report = query.data;
  const params = report.params ?? [];
  const generatedAt = report.meta?.generated_at
    ? formatDateTime(report.meta.generated_at)
    : 'just now';

  // A download must carry whatever params are currently applied so the export
  // matches the on-screen result exactly.
  const downloadSuffix = (format: 'csv' | 'xlsx' | 'pdf') => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(appliedParams)) {
      if (v !== '' && v != null) qs.set(k, v);
    }
    qs.set('format', format);
    return `/api/reports/${slug}?${qs.toString()}`;
  };

  const setDraft = (name: string, value: string) =>
    setDraftParams(prev => ({ ...prev, [name]: value }));

  const applyParams = () => setAppliedParams({ ...draftParams });
  const resetParams = () => { setDraftParams({}); setAppliedParams({}); };

  const paramsDirty = params.some(p => (draftParams[p.name] ?? '') !== (appliedParams[p.name] ?? ''));
  const anyApplied = Object.values(appliedParams).some(v => v !== '' && v != null);

  const toggleColumn = (name: string) =>
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });

  return (
    <>
      <PageHeader
        title={report.label}
        subtitle={report.description || undefined}
        actions={
          <>
            <Button variant="secondary" size="md" onClick={() => setShowColumnPicker(v => !v)}>
              Columns{hiddenCols.size > 0 ? ` (${reportColumns.length - hiddenCols.size}/${reportColumns.length})` : ''}
            </Button>
            <Button variant="secondary" size="md" onClick={() => query.refetch()}>
              Refresh
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => window.location.assign(downloadSuffix('csv'))}
            >
              Download CSV
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => window.location.assign(downloadSuffix('xlsx'))}
            >
              Export XLSX
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => window.location.assign(downloadSuffix('pdf'))}
            >
              Export PDF
            </Button>
          </>
        }
      />

      {params.length > 0 && (
        <ParamBar
          params={params}
          draft={draftParams}
          onChange={setDraft}
          onApply={applyParams}
          onReset={resetParams}
          dirty={paramsDirty}
          anyApplied={anyApplied}
        />
      )}

      {showColumnPicker && reportColumns.length > 0 && (
        <ColumnPicker
          columns={reportColumns}
          hidden={hiddenCols}
          onToggle={toggleColumn}
          onShowAll={() => setHiddenCols(new Set())}
        />
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-5)',
      }}>
        <StatCard label="Rows" value={String(report.rows.length)} caption="returned by query" accent="hunter" />
        <StatCard label="Columns" value={`${columns.length}/${reportColumns.length}`} caption="shown / in schema" accent="info" />
        <StatCard label="Generated" value={generatedAt} caption="server-side timestamp" accent="amber" />
        <StatCard label="Slug" value={slug || '—'} caption="report id" accent="green" />
      </div>

      {report.rows.length === 0 ? (
        <Empty />
      ) : (
        <DataTable data={report.rows} columns={columns} onRowClick={rowNav} />
      )}
    </>
  );
}

/** Year-over-year / range parameter controls, wired to the report's real params. */
function ParamBar({
  params, draft, onChange, onApply, onReset, dirty, anyApplied,
}: {
  params: ReportParam[];
  draft: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onApply: () => void;
  onReset: () => void;
  dirty: boolean;
  anyApplied: boolean;
}) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onApply(); }}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 'var(--sp-3)',
        flexWrap: 'wrap',
        padding: 'var(--sp-4)',
        marginBottom: 'var(--sp-4)',
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
      }}
    >
      {params.map(p => {
        const isNumeric = p.type === 'integer' || p.type === 'number';
        return (
          <label key={p.name} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span style={{
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)',
              color: 'var(--color-ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--ls-wide)',
            }}>
              {prettyLabel(p.name)}{p.required ? ' *' : ''}
            </span>
            <Input
              type={isNumeric ? 'number' : p.type === 'date' ? 'date' : 'text'}
              value={draft[p.name] ?? ''}
              required={p.required}
              onChange={(e) => onChange(p.name, e.target.value)}
              placeholder={p.default != null ? String(p.default) : (isNumeric ? 'e.g. 2024' : '')}
              style={{ width: 160 }}
            />
          </label>
        );
      })}
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        <Button type="submit" variant="primary" size="md" disabled={!dirty}>
          Apply
        </Button>
        {anyApplied && (
          <Button type="button" variant="secondary" size="md" onClick={onReset}>
            Reset
          </Button>
        )}
      </div>
    </form>
  );
}

/** Show/hide the report's result columns (list-view column selection). */
function ColumnPicker({
  columns, hidden, onToggle, onShowAll,
}: {
  columns: ReportColumn[];
  hidden: Set<string>;
  onToggle: (name: string) => void;
  onShowAll: () => void;
}) {
  return (
    <div style={{
      padding: 'var(--sp-4)',
      marginBottom: 'var(--sp-4)',
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--sp-3)',
      }}>
        <span style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--color-ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wide)',
        }}>Columns shown</span>
        {hidden.size > 0 && (
          <Button variant="secondary" size="sm" onClick={onShowAll}>Show all</Button>
        )}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
        gap: 'var(--sp-2)',
      }}>
        {columns.map(col => {
          const name = columnName(col);
          return (
            <Checkbox
              key={name}
              label={col.label}
              checked={!hidden.has(name)}
              onChange={() => onToggle(name)}
            />
          );
        })}
      </div>
    </div>
  );
}

function prettyLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatCell(col: ReportColumn, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (col.type === 'currency' || col.type === 'number') {
    // A COUNT()/SUM() comes back as a numeric STRING from node-postgres (bigint),
    // so coerce before formatting — otherwise big counts render without separators.
    const n = typeof value === 'number' ? value : (typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN);
    if (Number.isFinite(n)) return col.type === 'currency' ? `$${n.toLocaleString()}` : n.toLocaleString();
  }
  if (col.type === 'date' && typeof value === 'string') {
    // Strip any time portion so a timestamp-shaped value renders as YYYY-MM-DD
    // (no spurious "12:00 AM"); a bare date passes through unchanged.
    return value.length > 10 ? value.slice(0, 10) : value;
  }
  if (typeof value === 'string' && value.length > 80) {
    return value.slice(0, 80) + '…';
  }
  return String(value);
}

function Empty() {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No rows</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        This report returned an empty result set for the current org.
      </p>
    </div>
  );
}
